import { CKEditorContext, CKEditor as Editor } from "@ckeditor/ckeditor5-react";
import { Box, Typography } from "@mui/material";
import {
  Alignment,
  Base64UploadAdapter,
  BlockQuote,
  Bold,
  ClassicEditor,
  Code,
  Context,
  ContextWatchdog,
  Essentials,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  HtmlEmbed,
  Image,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SourceEditing,
  SpecialCharacters,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCellProperties,
  TableEditing,
  TableProperties,
  TableToolbar,
  Underline,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

function CKEditor({
  value,
  onChange,
  ref,
  onReady,
  placeholder,
  toolbar,
  disabled,
  charLimit,
  isEmailEditor = false,
  onFocus,
  onBlur,
}: {
  value: string;
  onChange: (value: any) => void;
  onReady: (value: any) => void;
  ref?: any;
  charLimit?: any;
  disabled?: boolean;
  placeholder?: string;
  toolbar?: string[];
  isEmailEditor?: boolean;
  onFocus?: any;
  onBlur?: any;
}) {
  const emailPlugins = [
    PasteFromOffice,
    RemoveFormat,
    SpecialCharacters,
    TableProperties,
    HtmlEmbed,
    GeneralHtmlSupport,
    FontFamily, // Better font control for emails
  ];

  const emailToolbarItems = [
    "fontFamily", // Font selection (important for emails)
    "removeFormat", // Clean styling
    "htmlEmbed", // Insert raw HTML
  ];

  const defaultPlugins = [
    Essentials,
    Bold,
    Italic,
    Paragraph,
    Table,
    Base64UploadAdapter,
    FontColor,
    Alignment,
    FontBackgroundColor,
    Image,
    ImageInsert,
    ImageUpload, // Required for upload button
    ImageResize,
    ImageStyle,
    ImageToolbar,
    FontSize,
    Underline,
    Strikethrough,
    BlockQuote,
    List,
    Subscript,
    Superscript,
    Link,
    Code,
    Heading,
    TableCellProperties,
    TableEditing,
    TableToolbar,
    SourceEditing,
  ];

  const plugins = isEmailEditor
    ? [...defaultPlugins, ...emailPlugins]
    : defaultPlugins;

  const defaultToolbar = toolbar || [
    "undo",
    "redo",
    "|",
    "heading",
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "blockQuote",
    "|",
    "fontSize",
    "fontColor",
    "fontBackgroundColor",
    "|",
    "alignment",
    "subscript",
    "superscript",
    "link",
    "code",
    "|",
    "bulletedList",
    "numberedList",
    "imageUpload",
    "insertTable",
    "tableColumn",
    "tableRow",
    "mergeTableCells",
    "tableToolbar",
    "sourceEditing",
  ];

  const toolbarItems = isEmailEditor
    ? [...defaultToolbar, ...emailToolbarItems]
    : defaultToolbar;

  return (
    <CKEditorContext context={Context} contextWatchdog={ContextWatchdog}>
      <Editor
        editor={ClassicEditor}
        ref={ref}
        disabled={disabled}
        config={{
          placeholder: placeholder,
          licenseKey: "GPL",
          plugins: plugins,
          toolbar: {
            items: toolbarItems,
            shouldNotGroupWhenFull: true,
          },
          image: {
            toolbar: [
              "imageStyle:inline",
              "imageStyle:block",
              "imageStyle:side",
              "|",
              "toggleImageCaption",
              "imageTextAlternative",
              "|",
              "imageResize:50",
              "imageResize:75",
              "imageResize:original",
            ],
            resizeUnit: "%",
            resizeOptions: [
              {
                name: "imageResize:original",
                value: null,
                icon: "original",
              },
              {
                name: "imageResize:50",
                value: "50",
                icon: "medium",
              },
              {
                name: "imageResize:75",
                value: "75",
                icon: "large",
              },
            ],
            upload: {
              types: ["jpeg", "png", "gif", "webp"], // Allowed image formats
            },
          },
          htmlSupport: isEmailEditor
            ? {
                allow: [
                  {
                    name: /.*/,
                    attributes: true,
                    classes: true,
                    styles: true,
                  },
                ],
              }
            : undefined,
          fontFamily: isEmailEditor
            ? {
                options: [
                  "Arial, sans-serif",
                  "Verdana, sans-serif",
                  "Helvetica, sans-serif",
                  "Times New Roman, serif",
                  "Courier New, monospace",
                  "Georgia, serif",
                ],
              }
            : undefined,
        }}
        data={value}
        onChange={(event: any, editor: any) => {
          const data = editor.getData();
          onChange(data);
        }}
        onReady={onReady}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {charLimit && (
        <Box display={"flex"} justifyContent={"flex-end"} p={1}>
          <Typography
            fontWeight={600}
            color={
              value?.length > charLimit
                ? "var(--warning-700)"
                : "var(--grey-500)"
            }
          >{`${value?.length} / ${charLimit}`}</Typography>
        </Box>
      )}
    </CKEditorContext>
  );
}

export default CKEditor;
